import { useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { StockHistory } from '../../interfaces/Report/StockHistoryReport/StockHistory';
import {
  getDefaultStockHistoryReportFilter,
  StockHistoryReportFilter,
} from '../../interfaces/Report/StockHistoryReport/StockHistoryReportFilter';
import { getStockHistoryReport } from '../../api/Report';
import { Pagination } from '../../interfaces/Pagination';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { dateTimePickerStyle } from '../SalesReport/SalesReportPage';
import { ProductsDropdown } from '../../components/Dropdowns/ProductsDropdown';
import { StockHistoryReportTable } from './StockHistoryReportTable';
import { OptionValueDropdown } from './OptionValueDropdown';

export const StockHistoryReportPage = () => {
  const [report, setReport] = useState<PaginatedResponse<StockHistory>>({
    data: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(getDefaultStockHistoryReportFilter());
  const [loading, setLoading] = useState(false);

  const refreshReport = (filters?: StockHistoryReportFilter) => {
    const appliedFilters = filters ?? filter;

    setLoading(true);
    getStockHistoryReport(appliedFilters)
      .then(setReport)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleSearch = () => {
    const newFilter = {
      ...filter,
      pagination: {
        ...filter.pagination,
        page: 0,
      },
    };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, my: 'auto' }}>
          Stok Hareketleri
        </Typography>
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box sx={dateTimePickerStyle}>
            <ProductsDropdown
              label='Ürün'
              multiple={false}
              value={filter.productID || undefined}
              onChange={(x) =>
                setFilter({
                  ...filter,
                  productID: x ?? 0,
                  optionValueID: undefined,
                })
              }
            />
          </Box>

          <Box sx={dateTimePickerStyle}>
            <OptionValueDropdown
              productID={filter.productID}
              value={filter.optionValueID}
              onChange={(x) => setFilter({ ...filter, optionValueID: x })}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  ...getDefaultStockHistoryReportFilter(),
                  pagination: filter.pagination,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtrele
            </Button>
          </Box>
        </Box>
      </Paper>

      <StockHistoryReportTable
        rows={report.data}
        totalCount={report.totalCount}
        pagination={filter.pagination}
        onPagination={handlePagination}
      />
    </Box>
  );
};
